import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { StoreErrorHandler } from '@zero/shared/util-global-state';
import { catchError, map, of, switchMap, tap } from 'rxjs';

import { ContractConfirmationInfrastructure } from '../infrastructure/contract-confirmation.infrastructure';
import { ContractConfirmationServiceBase } from '../services/contract-confirmation.service';
import { contractConfirmationActions, contractConfirmationApiActions } from './contract-confirmation.actions';
import { contractConfirmationFeature } from './contract-confirmation.reducer';

@Injectable()
export class ContractConfirmationEffects {
  private readonly _actions$ = inject(Actions);
  private readonly _contractConfirmationInfrastructure = inject(ContractConfirmationInfrastructure);
  private readonly _store = inject(Store);
  private readonly _storeErrorHandler = inject(StoreErrorHandler);
  private readonly _contractConfirmationService = inject(ContractConfirmationServiceBase);

  public loadContractConfirmations$ = createEffect(() => {
    return this._actions$.pipe(
      ofType(contractConfirmationActions.loadRequiredContractConfirmations),
      switchMap(() =>
        this._contractConfirmationInfrastructure.loadRequiredContractConfirmations().pipe(
          map((contractConfirmations) =>
            contractConfirmationApiActions.loadRequiredContractConfirmationsSuccess({ contractConfirmations }),
          ),
          catchError((error: HttpErrorResponse) => of(contractConfirmationApiActions.loadRequiredContractConfirmationsFailure({ error }))),
        ),
      ),
      catchError((error: HttpErrorResponse) => of(contractConfirmationApiActions.loadRequiredContractConfirmationsFailure({ error }))),
    );
  });

  public acceptContracts$ = createEffect(() => {
    return this._actions$.pipe(
      ofType(contractConfirmationActions.acceptContracts),
      concatLatestFrom(() => this._store.select(contractConfirmationFeature.selectContractConfirmations)),
      switchMap(([, contractConfirmations]) =>
        this._contractConfirmationInfrastructure.acceptContracts(contractConfirmations).pipe(
          map(() => contractConfirmationApiActions.acceptContractsSuccess()),
          catchError((error: HttpErrorResponse) => of(contractConfirmationApiActions.acceptContractsFailure({ error }))),
        ),
      ),
      catchError((error: HttpErrorResponse) => of(contractConfirmationApiActions.acceptContractsFailure({ error }))),
    );
  });

  public displayDialog$ = createEffect(
    () => {
      return this._actions$.pipe(
        ofType(contractConfirmationApiActions.loadRequiredContractConfirmationsSuccess),
        tap(({ contractConfirmations }) => {
          if (contractConfirmations.length > 0) {
            this._contractConfirmationService.displayDialog();
          }
        }),
      );
    },
    { dispatch: false },
  );

  public handleError$ = createEffect(
    () => {
      return this._actions$.pipe(
        ofType(
          contractConfirmationApiActions.acceptContractsFailure,
          contractConfirmationApiActions.loadRequiredContractConfirmationsFailure,
        ),
        tap((error) => this._storeErrorHandler.handleBackendError(error)),
      );
    },
    { dispatch: false },
  );
}
